import React, { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader } from '@/common/components/Card.tsx'
import { Input } from '@/app/common/components/input/Input'
import { Button } from '@/app/common/components/button/Button'
import ImageInput from '@/common/components/ImageInput.tsx'
import { CommonQueries } from '@/queries/common/queries.ts'
import { UpdateUserData } from '@/queries/users/requests.ts'
import { useQuery } from '@tanstack/react-query'
import { UserDetails } from '@/queries/users/requests.ts'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { SubmitHandler, useForm } from 'react-hook-form'
import FormErrorMessage from '@/common/components/FormErrorMessage.tsx'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'
import { useNavigate } from 'react-router-dom'
import { EllipsesLoader } from '@/common/components/EllipsesLoader.tsx'
import { useSelector, useDispatch } from 'react-redux'
import { AppDispatch, RootState } from '@/storage/store.ts'
import { refreshMe } from '@/storage/user/authSlice.ts'

const UserDataSchema = z.object({
  profile_picture: z.string().optional(),
  profile_picture_image: z.instanceof(File).optional(),
  first_name: z
    .string()
    .min(1, { message: 'First name is required' })
    .regex(/^[A-Za-z]+$/, {
      message: 'Only alphabet characters are allowed',
    }),
  last_name: z
    .string()
    .min(1, { message: 'Last name is required' })
    .regex(/^[A-Za-z]+$/, {
      message: 'Only alphabet characters are allowed',
    }),
  status: z.number().optional(),
  language_preference_id: z.number().optional(),
  global_country_id: z.number().min(1, { message: 'Country is required' }),
  city: z.string().min(1, { message: 'City is required' }),
  global_state_id: z.number().min(1, { message: 'State is required' }),
})

type UserFormData = z.infer<typeof UserDataSchema>
type UserFormField =
  | 'status'
  | 'profile_picture_image'
  | 'global_country_id'
  | 'language_preference_id'
  | 'global_state_id'
  | 'first_name'
  | 'last_name'
  | 'city'

const PersonalInfo: React.FC<UserDetails> = (UserData) => {
  const user = useSelector((state: RootState) => state.auth.user)
  const userRoles = useSelector((state: RootState) => state.auth.roles)
  const navigate = useNavigate()
  const { fireAlert } = useAppAlert()
  const [inviteIsFetching, setInviteIsFetching] = useState(false)
  const [shouldDeletePhoto, setShouldDeletePhoto] = useState(false)
  const StoreDispatch = useDispatch<AppDispatch>()
  const [formData, setFormData] = useState<{
    profile_picture_image: File | null
    id: number
    status: number
    global_country_id: number | null
    language_preference_id: number | null
    global_state_id: number | null
    first_name: string | null
    last_name: string | null
    city: string | null
    profile_picture: string | null
  }>({
    ...UserData,
    profile_picture_image: null,
  })

  const isLoginUserProfile = () => {
    return user?.id == UserData?.id
  }
  const canEditUserStatus =
    userRoles.includes('admin') || userRoles.includes('program_manager')

  const ContryList = useQuery(CommonQueries.FetchCommonCountries())
  const StateList = useQuery(
    CommonQueries.FetchStateByContry(formData.global_country_id ?? 3)
  )
  const handleImageChange = (file: File | null) => {
    setFormData((prevData) => ({
      ...prevData,
      ['profile_picture_image']: file,
    }))
    setValue('profile_picture_image', file ?? undefined)
    setShouldDeletePhoto(file === null ? true : false)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prevData) => ({ ...prevData, [name]: value }))
    setValue(name as UserFormField, value.trim())
    trigger(name as UserFormField)
  }
  const handleSelectChange = (name: keyof UserDetails, value: number) => {
    setFormData((prevData) => ({ ...prevData, [name]: value }))
    setValue(name as UserFormField, value)
    trigger(name as UserFormField)
  }

  useEffect(() => {
    StateList.refetch()
  }, [formData.global_country_id])

  useEffect(() => {
    setValue('status', UserData.status ?? 1)
    setValue('global_country_id', UserData.global_country_id ?? 3)
    setValue('language_preference_id', UserData.language_preference_id ?? 0)
    setValue('global_state_id', UserData.global_state_id ?? 0)
    setValue('first_name', UserData.first_name ?? '')
    setValue('last_name', UserData.last_name ?? '')
    setValue('city', UserData.city ?? '')
  }, [UserData])

  const {
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    trigger,
  } = useForm<UserFormData>({
    resolver: zodResolver(UserDataSchema),
  })

  const onSubmit: SubmitHandler<UserFormData> = async (FformData) => {
    setInviteIsFetching(true)
    const { error } = await UpdateUserData(
      {
        profile_picture_image: shouldDeletePhoto
          ? null
          : FformData.profile_picture_image || undefined,
        profile_picture: shouldDeletePhoto
          ? null
          : FformData.profile_picture || undefined,
        status: FformData.status,
        global_country_id: FformData.global_country_id,
        language_preference_id: FformData.language_preference_id,
        global_state_id: FformData.global_state_id,
        first_name: FformData.first_name,
        last_name: FformData.last_name,
        city: FformData.city,
      },
      formData?.id
    )

    setInviteIsFetching(false)
    if (error) {
      await fireAlert({
        variant: 'error',
        message: error.message,
      })
      return
    } else {
      if (isLoginUserProfile()) {
        StoreDispatch(refreshMe())
      }
      await fireAlert({
        variant: 'success',
        message: `${isLoginUserProfile() ? 'Profile' : 'User'} updated successfully`,
      })
      reset()
      if (UserData.callBackUrl && UserData.callBackUrl != '') {
        navigate(UserData.callBackUrl)
      } else {
        window.location.reload()
      }
    }
  }

  useEffect(() => {
    if (shouldDeletePhoto) {
      setFormData((prevData) => ({
        ...prevData,
        profile_picture_image: null,
        profile_picture: null,
      }))
    }
  }, [shouldDeletePhoto])

  const languageOptions = [
    { value: 1, label: 'English' },
    { value: 2, label: 'Arabic' },
    { value: 3, label: 'Chinese' },
    { value: 4, label: 'Dutch' },
    { value: 5, label: 'Finnish' },
    { value: 6, label: 'French' },
    { value: 7, label: 'German' },
    { value: 8, label: 'Greek' },
    { value: 9, label: 'Hindi' },
    { value: 10, label: 'Italian' },
    { value: 11, label: 'Japanese' },
    { value: 12, label: 'Korean' },
    { value: 13, label: 'Norwegian' },
    { value: 14, label: 'Portuguese' },
    { value: 15, label: 'Russian' },
    { value: 16, label: 'Spanish' },
    { value: 17, label: 'Swedish' },
  ]

  const StatusOptions = [
    { value: '1', label: 'Active' },
    { value: '2', label: 'Inactive' },
    { value: '3', label: 'Pending' },
  ]

  return (
    <Card className="mt-4" id={'pesonal_info'}>
      <CardHeader className="flex justify-between items-center text-md font-semibold">
        <div className="text-left text-md font-semibold text-gray-900">
          Personal Info
        </div>
      </CardHeader>
      <CardContent className="p-0 lg:p-0 card-body grid gap-5">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="px-4 py-6 sm:p-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium">Photo</h2>
              <span className="py-2 text-gray-500 font-normal min-w-32 text-2sm">
                150x150px JPEG, PNG Image
              </span>
              <div className="w-20 h-20">
                <ImageInput
                  onImageChange={handleImageChange}
                  imageUrl={formData.profile_picture ?? ''}
                  firstName={formData.first_name}
                  lastName={formData.last_name}
                />
              </div>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-1">
                  <label className="block text-sm font-medium mb-1">
                    First Name<span className="text-danger ml-1">*</span>
                  </label>
                  <Input
                    type="text"
                    name="first_name"
                    value={formData.first_name ?? ''}
                    onChange={handleInputChange}
                  />
                  <FormErrorMessage message={errors.first_name?.message} />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium mb-1">
                    Last Name<span className="text-danger ml-1">*</span>
                  </label>
                  <Input
                    type="text"
                    name="last_name"
                    value={formData.last_name ?? ''}
                    onChange={handleInputChange}
                  />
                  <FormErrorMessage message={errors.last_name?.message} />
                </div>
              </div>
              {!isLoginUserProfile() && canEditUserStatus && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                    defaultValue={formData.status ?? 1}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      const selectedValue = Number(e.target.value)
                      handleSelectChange('status', selectedValue) // Update the form state
                    }}
                  >
                    {StatusOptions.filter(
                      (status) => status.label != 'Pending'
                    ).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <FormErrorMessage message={errors.status?.message} />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Language
                </label>
                <select
                  name="language_preference_id"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  value={formData.language_preference_id ?? 1}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    const selectedValue = Number(e.target.value)
                    handleSelectChange('language_preference_id', selectedValue) // Update the form state
                  }}
                >
                  <option value="">Select Language</option>
                  {languageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <FormErrorMessage
                  message={errors.language_preference_id?.message}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Location<span className="text-danger ml-1">*</span>
                </label>
                <div className="flex justify-between items-center gap-5">
                  <div className="block w-full">
                    <select
                      name="global_country_id"
                      value={formData.global_country_id ?? 3}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const selectedValue = Number(e.target.value)
                        handleSelectChange('global_country_id', selectedValue) // Update the form state
                      }}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                    >
                      <option value="">Select Country</option>
                      {ContryList?.data?.data.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                    <FormErrorMessage
                      message={errors.global_country_id?.message}
                    />
                  </div>
                  <div className="block w-full">
                    <Input
                      type="text"
                      name="city"
                      placeholder="City"
                      value={formData.city ?? ''}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage message={errors.city?.message} />
                  </div>
                  <div className="block w-full">
                    <select
                      name="global_state_id"
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.global_state_id ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const selectedValue = Number(e.target.value)
                        handleSelectChange('global_state_id', selectedValue) // Update the form state
                      }}
                    >
                      <option value="">Select State</option>
                      {StateList.data?.data.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                    <FormErrorMessage
                      message={errors.global_state_id?.message}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                {inviteIsFetching ? (
                  <EllipsesLoader size="md" />
                ) : (
                  <Button type="submit">Save Changes</Button>
                )}
              </div>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default PersonalInfo
